import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import type { ItemCategory } from "../../api/types";
import { useAuth } from "../../state/AuthContext";
import { Modal } from "../Modal";
import { MarginMultiplierHint } from "../MarginMultiplierHint";

type RowDraft = { vat: string; margin: string };

/**
 * G-30 + G-45 — the owner's "Tax & pricing rules" entry point, on the Finances
 * page by owner decision (Finances is the business-settings home).
 *
 * Every category is listed at once with both numbers editable inline: seeing
 * the whole list is what makes the assignment self-checking — the real risk is
 * a category sitting in the wrong band (ARTIFICIAL_FLOWER at 10% would be the
 * classic mistake), not a typo in one number.
 *
 * Neither change is retroactive. The VAT rate is snapshotted onto each sale
 * line at sale time and historical rows are never recomputed (§4); the target
 * margin only shapes the guidance a florist sees from now on, so nothing
 * already sold is re-judged against a margin the shop did not have at the time.
 */
export function TaxRatesCard() {
  const { hasRole } = useAuth();
  const isOwner = hasRole("OWNER");

  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState<ItemCategory[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await api.listCategories();
      setCategories(rows);
      setDrafts(Object.fromEntries(rows.map((c) => [c.id, draftFor(c)])));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load categories");
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  if (!isOwner) return null;

  const saveRow = async (category: ItemCategory) => {
    const draft = drafts[category.id] ?? draftFor(category);
    const saved = draftFor(category);

    const vat = Number(draft.vat.trim());
    if (!draft.vat.trim() || !Number.isFinite(vat) || vat < 0 || vat > 100) {
      setError(`"${category.label}": enter a VAT rate between 0 and 100`);
      return;
    }
    // Bounded BELOW 100: the price formula divides by (1 - m/100), so 100 is an
    // infinite price and anything above it turns the price negative.
    const margin = Number(draft.margin.trim());
    if (
      !draft.margin.trim() ||
      !Number.isFinite(margin) ||
      margin < 0 ||
      margin >= 100
    ) {
      setError(`"${category.label}": enter a target margin of 0 or more and under 100`);
      return;
    }

    setSavingId(category.id);
    setError(null);
    setSavedId(null);
    try {
      // Two endpoints, and only the ones that actually changed are called —
      // a margin edit must never rewrite a VAT rate as a side effect.
      let updated: ItemCategory = category;
      if (draft.vat !== saved.vat) {
        updated = await api.updateCategoryVatRate(category.id, vat);
      }
      if (draft.margin !== saved.margin) {
        updated = await api.updateCategoryTargetMargin(category.id, margin);
      }
      setCategories((prev) =>
        prev ? prev.map((c) => (c.id === updated.id ? updated : c)) : prev,
      );
      setDrafts((prev) => ({ ...prev, [updated.id]: draftFor(updated) }));
      setSavedId(category.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSavingId(null);
    }
  };

  const setField = (id: string, field: keyof RowDraft, value: string) => {
    setSavedId(null);
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { vat: "", margin: "" }), [field]: value },
    }));
  };

  return (
    <div className="card opcost-card">
      <div className="opcost-card-head">
        <div>
          <h3 className="opcost-card-title">Tax &amp; pricing rules</h3>
          <p className="opcost-card-subtitle">
            Per category: IVA rate · target margin (a share of the price before IVA) · future sales only
          </p>
        </div>
        <button type="button" className="button tinted" onClick={() => setOpen(true)}>
          Change tax and pricing rules
        </button>
      </div>

      {open ? (
        <Modal title="Tax &amp; pricing rules per category" onClose={() => setOpen(false)}>
          <div className="taxrate-list">
            {error ? <p className="taxrate-error" role="alert">{error}</p> : null}
            {categories === null && !error ? <p className="muted">Loading…</p> : null}
            {categories?.map((category) => {
              const draft = drafts[category.id] ?? { vat: "", margin: "" };
              const saved = draftFor(category);
              const dirty = draft.vat !== saved.vat || draft.margin !== saved.margin;
              return (
                <div key={category.id} className="taxrate-row">
                  <span className="taxrate-label">{category.label}</span>
                  <span className="taxrate-input-wrap">
                    <input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={100}
                      step={0.01}
                      value={draft.vat}
                      aria-label={`VAT percent for ${category.label}`}
                      onChange={(e) => setField(category.id, "vat", e.target.value)}
                    />
                    <span aria-hidden="true">% VAT</span>
                  </span>
                  <span className="taxrate-input-wrap">
                    <input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={99.99}
                      step={0.01}
                      value={draft.margin}
                      aria-label={`Target margin percent for ${category.label}`}
                      onChange={(e) => setField(category.id, "margin", e.target.value)}
                    />
                    <span aria-hidden="true">% target margin</span>
                  </span>
                  {dirty ? (
                    <button
                      type="button"
                      className="button primary taxrate-save"
                      disabled={savingId === category.id}
                      onClick={() => void saveRow(category)}
                    >
                      {savingId === category.id ? "Saving…" : "Save"}
                    </button>
                  ) : (
                    <span className="taxrate-status">
                      {savedId === category.id ? "Saved" : ""}
                    </span>
                  )}
                  <MarginMultiplierHint
                    targetMarginPercent={draft.margin}
                    vatRatePercent={draft.vat}
                    className="taxrate-multiplier muted"
                  />
                </div>
              );
            })}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function draftFor(category: ItemCategory): RowDraft {
  return {
    vat: formatRate(category.vatRatePercent),
    margin: formatRate(category.targetMarginPercent),
  };
}

/** "10.00" → "10", "10.50" → "10.5" — what the owner typed, not what Decimal stored. */
function formatRate(rate: string | undefined): string {
  if (rate === undefined) return "";
  const n = Number(rate);
  return Number.isFinite(n) ? String(n) : rate;
}
